import { Box, Drawer, List } from '@mui/material';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { ViewContext, useUserStore } from 'src/store/user-store';
import { Route } from 'src/types/interfaces/route';
import { getRoutes as siteRoutes } from 'src/view-contexts/site/constants';
import { Scrollbar } from '../atoms/scrollbar';
import SidebarItems from '../molecules/sidebar/sidebar-items';

interface SidebarProps {
  onPin?: () => void;
  pinned: boolean;
}

const getRoutesForViewContext = (currentViewContext: ViewContext) => {
  switch (currentViewContext) {
    case 'site':
      return siteRoutes();
  }
};

const SidebarComponent: FC<SidebarProps> = props => {
  const { pinned } = props;
  const { pathname } = useLocation();
  const { viewContext } = useUserStore();

  const sections = useMemo(
    () => getRoutesForViewContext(viewContext ?? 'site') ?? [],
    [viewContext],
  );

  const routes = useMemo(
    () => sections?.flatMap(route => route.routes),
    [sections],
  );

  const [activeRoute, setActiveRoute] = useState<Route | null>(null);

  useEffect(() => {
    routes?.forEach(route => {
      const active = matchPath(
        { path: route.href ?? '', end: false },
        pathname,
      );

      if (active) {
        setActiveRoute(route);
      }
    });
  }, [pathname, routes]);

  return (
    <>
      <Drawer
        open
        variant="permanent"
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            overflowX: 'hidden',
            top: 64,
            pb: 3,
            zIndex: 1,
            transition: 'width 250ms ease-in-out',
            width: pinned ? 270 : 73,
            '&:hover': {
              width: 270,
              '& span, p': {
                display: 'flex',
              },
            },
          },
        }}>
        <Scrollbar
          style={{
            display: 'flex',
            flex: 1,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              p: 2,
              pt: 2.5,
              pb: 7,
            }}>
            {/* 
            //TO DO: removed for now - might be added in the future
            <Can I="create" a="Site" passThrough>
              {allowed =>
                allowed ? (
                  <Tooltip
                    title={t('common:notSufficientPermissions')}
                    disableHoverListener={allowed}
                    disableFocusListener={allowed}>
                    <span>
                      <Box sx={{ my: 1 }}>
                        <Button
                          sx={{
                            color: '#6EBAD3',
                            borderBottom: '1px solid #6EBAD3',
                            borderRadius: 0,
                          }}
                          variant="text"
                          disabled={!allowed}
                          onClick={() => navigate('/create-site')}
                          startIcon={<Plus />}>
                          {t('createSite:general.actions.addNewSite')}
                        </Button>
                      </Box>
                    </span>
                  </Tooltip>
                ) : null
              }
            </Can> */}

            <List
              disablePadding
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sections.map(section => (
                <SidebarItems
                  key={section.title}
                  title={section.title}
                  routes={section.routes}
                  activeRoute={activeRoute}
                  pinned={pinned}
                />
              ))}
            </List>
          </Box>
        </Scrollbar>
      </Drawer>
    </>
  );
};

export const Sidebar = SidebarComponent;
